import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { CourseCard } from "@/components/CourseCard";
import { Input } from "@/components/ui/input";
import { Loader2, Search, SlidersHorizontal, ChevronDown, BookOpen, Hammer, GraduationCap } from "lucide-react";
import { PreviewBanner } from "@/components/PreviewBanner";

const GRADES = ["K", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];
const GRADE_LABEL = (g) => (g === "K" ? "K" : g);
const HERO = "https://images.unsplash.com/photo-1498674202614-ac0172c6c61a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGNoaWxkJTIwbGVhcm5pbmclMjBmcmllbmRseXxlbnwwfHx8fDE3ODQyMzc2Nzh8MA&ixlib=rb-4.1.0&q=85";

const GROUP_ORDER = ["core", "life_skills", "college_prep"];
const GROUP_META = {
  core: { label: "Core Subjects", color: "#3B82F6", icon: BookOpen },
  life_skills: { label: "Real-World Life Skills", color: "#CA8A04", icon: Hammer },
  college_prep: { label: "College Prep", color: "#7C3AED", icon: GraduationCap },
};

export default function Curriculum() {
  const [params, setParams] = useSearchParams();
  const [subjects, setSubjects] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState({ core: true, life_skills: false, college_prep: false });

  const grade = params.get("grade") || "";
  const subject = params.get("subject") || "";

  useEffect(() => {
    api.get("/subjects").then((r) => setSubjects(r.data)).catch(() => {});
  }, []);

  const grouped = useMemo(() => {
    const g = { core: [], life_skills: [], college_prep: [] };
    subjects.forEach((s) => { (g[s.category] || g.core).push(s); });
    const byName = (a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
    Object.values(g).forEach((arr) => arr.sort(byName));
    return g;
  }, [subjects]);

  // Auto-open the group that holds the currently-selected subject
  useEffect(() => {
    if (!subject || !subjects.length) return;
    const s = subjects.find((x) => x.subject_id === subject);
    if (s?.category) setOpen((o) => ({ ...o, [s.category]: true }));
  }, [subject, subjects]);

  const fetchCourses = useCallback(async () => {
    setLoading(true);
    const qs = {};
    if (grade) qs.grade = grade;
    if (subject) qs.subject_id = subject;
    if (q) qs.q = q;
    try {
      const { data } = await api.get("/courses", { params: qs });
      setCourses(data);
    } finally {
      setLoading(false);
    }
  }, [grade, subject, q]);

  useEffect(() => {
    const t = setTimeout(fetchCourses, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [fetchCourses, q]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };
  const toggleGroup = (g) => setOpen((o) => ({ ...o, [g]: !o[g] }));

  // Life Skills & College Prep are high-school / post-secondary — only show them for 9–12 or "All"
  const HS_GRADES = new Set(["9", "10", "11", "12"]);
  const groupVisible = (g) => (g === "core" ? true : (!grade || HS_GRADES.has(grade)));

  return (
    <div className="kid-theme min-h-screen">
      <div className="mx-auto max-w-7xl px-5 py-12 lg:px-8">
        <PreviewBanner />
        <div className="relative overflow-hidden rounded-3xl border-2 border-border bg-accent shadow-sm">
          <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover object-center opacity-30" />
          <div className="relative px-8 py-12 sm:py-16">
            <span className="label-caps text-secondary">K–12 Catalog</span>
            <h1 className="mt-2 font-head text-4xl font-black sm:text-5xl">Let's Explore &amp; Learn!</h1>
            <p className="mt-3 max-w-2xl text-base font-bold text-foreground/70 sm:text-lg">Browse every grade and subject. Pick a course, hop into a lesson, and start your adventure.</p>
          </div>
        </div>

        {/* Grades across the top */}
        <div className="mt-8">
          <div className="mb-3 flex items-center gap-2 text-muted-foreground">
            <SlidersHorizontal className="h-4 w-4" />
            <span className="label-caps">Grade</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilter("grade", "")}
              data-testid="filter-grade-all"
              className={`rounded-full border-2 px-4 py-2 label-caps transition-colors ${!grade ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary"}`}
            >All</button>
            {GRADES.map((g) => (
              <button
                key={g}
                onClick={() => setFilter("grade", g)}
                data-testid={`filter-grade-${g}`}
                className={`min-w-[46px] rounded-full border-2 px-3 py-2 label-caps transition-colors ${grade === g ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary"}`}
              >{GRADE_LABEL(g)}</button>
            ))}
          </div>
        </div>

        {/* Sidebar + results */}
        <div className="mt-8 grid gap-8 lg:grid-cols-[260px_1fr]">
          {/* Subject sidebar */}
          <aside className="lg:sticky lg:top-24 lg:h-fit">
            <button
              onClick={() => setFilter("subject", "")}
              data-testid="filter-subject-all"
              className={`mb-3 flex w-full items-center justify-between rounded-2xl border-2 px-4 py-3 font-head text-sm font-black transition-colors ${!subject ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary"}`}
            >All Subjects <span className="text-xs font-bold opacity-70">{subjects.length}</span></button>

            <div className="space-y-3">
              {GROUP_ORDER.map((g) => {
                const meta = GROUP_META[g];
                const items = grouped[g] || [];
                if (!items.length || !groupVisible(g)) return null;
                const Icon = meta.icon;
                return (
                  <div key={g} className="overflow-hidden rounded-2xl border-2 border-border bg-card">
                    <button
                      onClick={() => toggleGroup(g)}
                      data-testid={`subject-group-${g}`}
                      className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-muted/50"
                    >
                      <span className="flex items-center gap-2 font-head text-sm font-black" style={{ color: meta.color }}>
                        <Icon className="h-4 w-4" strokeWidth={2.75} /> {meta.label}
                        <span className="text-xs font-bold text-muted-foreground">({items.length})</span>
                      </span>
                      <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open[g] ? "rotate-180" : ""}`} />
                    </button>
                    {open[g] && (
                      <div className="border-t-2 border-border p-2">
                        {items.map((s) => (
                          <button
                            key={s.subject_id}
                            onClick={() => setFilter("subject", s.subject_id)}
                            data-testid={`filter-subject-${s.subject_id}`}
                            className={`flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2 text-left text-sm font-bold transition-colors ${subject === s.subject_id ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted"}`}
                          >
                            <span>{s.name}</span>
                            {!s.course_count && <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[0.6rem] font-black uppercase tracking-wide text-amber-700">Soon</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </aside>

          {/* Results */}
          <div>
            <div className="relative mb-6 max-w-md">
              <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search courses..."
                data-testid="course-search-input"
                className="rounded-full border-2 border-border bg-card py-6 pl-11 font-semibold"
              />
            </div>

            {loading ? (
              <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
            ) : courses.length === 0 ? (
              <div className="rounded-3xl border-2 border-border bg-card p-16 text-center text-lg font-bold text-muted-foreground" data-testid="no-courses">
                No courses match your filters.
              </div>
            ) : (
              <>
                <p className="mb-6 label-caps text-muted-foreground" data-testid="course-count">{courses.length} Courses</p>
                <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3 stagger">
                  {courses.map((c) => <CourseCard key={c.course_id} course={c} />)}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
